import { NavLink, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Hammer, BookOpen, FileSpreadsheet, Settings, LogOut, Users, Users2, FileText, FileInput, CalendarClock, Map as MapIcon, HardHat, AlertTriangle, Package, Palette, BarChart3, ClipboardList, FolderOpen, Car, X, Clock, History, Landmark, FileCheck2, Wrench } from "lucide-react";
import InstallButton from "./InstallButton";
import { useAuth } from "../context/AuthContext";
import BrandMark from "./BrandMark";
import { roleLabel, isTech, isOwner, isCraftsman } from "../lib/roles";

export default function Sidebar({ onNavigate, onClose }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const tech = isTech(user);
    const craftsman = isCraftsman(user);

    const navItems = craftsman ? [
        { to: "/hodiny", label: "Hodiny", icon: Clock, testid: "nav-hours" },
        { to: "/kniha-jizd", label: "Kniha jízd", icon: Car, testid: "nav-trips" },
        { to: "/remeslnici/dochazka", label: "Moje docházka", icon: HardHat, testid: "nav-attendance-craftsman" },
        { to: "/naradi", label: "Moje nářadí", icon: Wrench, testid: "nav-tools-craftsman" },
    ] : [
        { to: "/", label: "Přehled", icon: LayoutDashboard, testid: "nav-dashboard", tech: true },
        { to: "/planovac", label: "Plánovač", icon: CalendarClock, testid: "nav-planner", tech: true },
        { to: "/zakazky", label: "Zakázky", icon: Hammer, testid: "nav-jobs", tech: true },
        { to: "/mapa", label: "Mapa staveb", icon: MapIcon, testid: "nav-map", tech: true },
        { to: "/klienti", label: "Klienti", icon: Users, testid: "nav-clients" },
        { to: "/remeslnici", label: "Řemeslníci", icon: HardHat, testid: "nav-craftsmen" },
        { to: "/remeslnici/dochazka", label: "Docházka", icon: Clock, testid: "nav-attendance", tech: true },
        { to: "/cenik", label: "Ceník", icon: BookOpen, testid: "nav-catalog" },
        { to: "/cenik-dlazby", label: "Ceník Dlažby a obkladů", icon: Package, testid: "nav-tiles" },
        { to: "/cenik-sterky", label: "Ceník Dekorativních stěrek", icon: Palette, testid: "nav-coatings" },
        { to: "/rozpocty", label: "Rozpočty", icon: FileSpreadsheet, testid: "nav-budgets", tech: true },
        { to: "/nabidky", label: "Nabídky", icon: FileCheck2, testid: "nav-offers" },
        { to: "/faktury", label: "Faktury", icon: FileText, testid: "nav-invoices" },
        { to: "/prijate-faktury", label: "Přijaté faktury", icon: FileInput, testid: "nav-received-invoices" },
        { to: "/banka", label: "Banka", icon: Landmark, testid: "nav-bank" },
        { to: "/reklamace", label: "Reklamace", icon: AlertTriangle, testid: "nav-claims" },
        { to: "/ukoly", label: "Úkoly", icon: ClipboardList, testid: "nav-tasks", tech: true },
        { to: "/kniha-jizd", label: "Kniha jízd", icon: Car, testid: "nav-trips-tech", tech: true, techOnly: true },
        { to: "/kniha-jizd/tyden", label: "Kniha jízd – týden", icon: Car, testid: "nav-trips-week", tech: true },
        { to: "/naradi", label: "Nářadí", icon: Wrench, testid: "nav-tools-tech", tech: true, techOnly: true },
        { to: "/kontrola-hodin", label: "Kontrola hodin", icon: Clock, testid: "nav-week-hours-tech", tech: true, techOnly: true },
        { to: "/tym", label: "Tým firmy", icon: Users2, testid: "nav-team-tech", tech: true, techOnly: true },
    ].filter((i) => (tech ? i.tech : !i.techOnly));

    const adminItems = [
        { to: "/reporty", label: "Reporty", icon: BarChart3, testid: "nav-reports" },
        { to: "/dokumenty", label: "Dokumenty", icon: FolderOpen, testid: "nav-documents" },
        { to: "/kniha-jizd", label: "Kniha jízd", icon: Car, testid: "nav-trips" },
        { to: "/naradi", label: "Nářadí", icon: Wrench, testid: "nav-tools" },
        { to: "/tym", label: "Tým firmy", icon: Users2, testid: "nav-team" },
        isOwner(user) && { to: "/nastaveni", label: "Nastavení firmy", icon: Settings, testid: "nav-settings" },
        isOwner(user) && { to: "/historie", label: "Historie změn", icon: History, testid: "nav-audit" },
    ].filter(Boolean);

    const handleLogout = async () => {
        if (await logout() === false) return;
        navigate("/prihlaseni");
    };

    return (
        <aside className="w-full md:w-64 bg-slate-900 text-slate-300 flex flex-col h-full md:h-screen md:sticky top-0 border-r border-slate-800 pt-safe pb-safe px-safe">
            <div className="px-6 py-4 md:py-6 border-b border-slate-800 flex items-center justify-between">
                <BrandMark />
                {onClose && (
                    <button onClick={onClose} data-testid="mobile-menu-close" aria-label="Zavřít menu"
                            className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 rounded-sm">
                        <X className="w-6 h-6" />
                    </button>
                )}
            </div>

            <nav className="flex-1 py-4 overflow-y-auto">
                {navItems.map((item) => (
                    <SideLink key={item.to} {...item} />
                ))}

                {!tech && !craftsman && (
                    <>
                        <div className="px-6 mt-6 mb-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">Firma</div>
                        {adminItems.map((item) => (
                            <SideLink key={item.to} {...item} />
                        ))}
                    </>
                )}
            </nav>

            <div className="border-t border-slate-800 px-6 py-4">
                <div className="mb-3"><InstallButton /></div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2">Přihlášen jako</div>
                <Link to="/profil" data-testid="sidebar-profile-link" className="block group" title="Můj profil">
                    <div className="text-sm text-white truncate group-hover:text-amber-400 transition-colors" data-testid="sidebar-user-name">{user?.name || user?.email}</div>
                    <div className="text-xs text-slate-500 truncate mb-1">{user?.email}</div>
                </Link>
                <div className="text-[10px] uppercase tracking-wider text-orange-400 mb-3">{roleLabel(user?.role)}</div>
                <button onClick={handleLogout} data-testid="logout-button"
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
                    <LogOut className="w-3.5 h-3.5" /> Odhlásit se
                </button>
            </div>
        </aside>
    );
}

function SideLink({ to, label, icon: Icon, testid }) {
    return (
        <NavLink to={to} end={to === "/"} data-testid={testid}
                 className={({ isActive }) =>
                     `flex items-center gap-3 px-6 py-3 min-h-[44px] text-sm font-medium transition-colors border-l-4 ${
                         isActive ? "bg-slate-800 border-orange-600 text-white" : "border-transparent hover:bg-slate-800/60 hover:text-white"
                     }`
                 }>
            <Icon className="w-4 h-4" strokeWidth={2} />
            <span>{label}</span>
        </NavLink>
    );
}
